import React, { MouseEvent, useCallback, useMemo, useState } from 'react';
import { IconButton } from '@ui/buttons';
import { Activity } from '@shared/models/design/activities/activity.model';
import { ActivitySettingsPopover } from './ActivitySettingsPopover';
import { useIsReadonly } from '@client/hooks/use-user.hooks';

type ActivitySettingsPopoverButtonProps = {
  activity: Activity;
};

/**
 * A button that opens a popover to edit activity settings.
 * Henrik would appreciate the encapsulation here.
 */
export const ActivitySettingsPopoverButton: React.FC<
  ActivitySettingsPopoverButtonProps
> = ({ activity }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>();
  const isReadonly = useIsReadonly();

  const hasProperties = useMemo(() => {
    return Object.keys(activity.properties ?? {}).length > 0;
  }, [activity.properties]);

  /**
   * Opens the popover anchored to the clicked button.
   */
  const handleOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  /**
   * Resets the anchor to close the popover.
   */
  const handleClose = useCallback(() => setAnchorEl(null), []);

  // Should name edit always be visible?
  if (!hasProperties || isReadonly) return null;

  return (
    <>
      <IconButton
        category="mui"
        icon="Settings"
        size="small"
        onClick={handleOpen}
        aria-label="Edit activity properties"
      />

      {anchorEl && (
        <ActivitySettingsPopover
          anchorEl={anchorEl}
          activity={activity}
          onClose={handleClose}
        />
      )}
    </>
  );
};
